import {
  CircularProgress,
  Typography
} from "@mui/material";
import { Box } from "@mui/system";
import { BarChart } from "@mui/x-charts/BarChart";
import { useMemo } from "react";

// Type for multiple bar chart data
export interface MultipleBarChartData {
  xLabels: string[];
  series: {
    data: number[];
    id: string;
    label: string;
    color: string;
  }[];
}

/**
 * Multiple Bar Chart Component for comparing multiple metrics
 * 
 * @param data - Object with xLabels and series configuration
 * @param isLoading - Show loading spinner when true
 * @param title - Chart title
 * @param colors - Array of colors to override series colors (optional)
 * 
 * Example usage:
 * <LeadsMultipleBarChart 
 *   data={{
 *     xLabels: ["Omne by FWD", "Digital Commerce"],
 *     series: [
 *       { data: [2, 25], id: "apeData", label: "APE", color: "#ff8c00" },
 *       { data: [28, 15], id: "wonDealsData", label: "Won Deals", color: "#4caf50" }
 *     ]
 *   }} 
 *   title="APE vs Won Deals by Channel" 
 *   colors={["#ff8c00", "#4caf50"]}
 * />
 */
interface Props {
  data?: MultipleBarChartData | null;
  isLoading?: boolean;
  title?: string;
  colors?: string[];
  hasError?: boolean;
}

export default function LeadsMultipleBarChart({
  data,
  isLoading = false,
  title = "Chart",
  colors = ["#ff8c00", "#2196f3"],
  hasError = false,
}: Props) {
  const xLabels = useMemo(() => {
    if (!data?.xLabels || data.xLabels.length === 0) {
      return [];
    }
    const validLabels = data.xLabels.filter(label => label && typeof label === "string");
    return validLabels;
  }, [data]);

  const seriesData = useMemo(() => {
    if (!data?.series) return [];

    const maxValue = Math.max(...data.series.flatMap(s => s.data));
    const minDisplayValue = maxValue * 0.005; // 2% of max for visibility

    return data.series.map((series, index) => {
      // Store original data for tooltips
      const originalData = [...series.data];

      return {
        ...series,
        color: colors && colors[index] ? colors[index] : series.color,
        // Modified data for bar heights
        data: series.data.map(value =>
          value > 0 ? Math.max(value, minDisplayValue) : value
        ),
        // Show original values in tooltips
        valueFormatter: (value: number | null) => {
          if (value === null || value === undefined) return "";
          // Find the index of this value in the modified data
          const modifiedData = series.data.map(v =>
            v > 0 ? Math.max(v, minDisplayValue) : v
          );
          const dataIndex = modifiedData.indexOf(value);
          const originalValue = dataIndex >= 0 ? originalData[dataIndex] : value;

          if (originalValue >= 1000000) return `${(originalValue / 1000000).toFixed(1)}M`;
          if (originalValue >= 1000) return `${(originalValue / 1000).toFixed(0)}K`;
          return originalValue.toLocaleString();
        }
      };
    });
  }, [data, colors]);


  return (
    <Box sx={{
      width: "100%",
      height: "500px",
      border: "solid 1px #0000001F",
      borderRadius: "24px",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden"
    }}>
      <Box sx={{ padding: "16px 16px 0 16px" }}>
        <Typography variant="title_medium">{title}</Typography>
      </Box>
      {
        isLoading ? (<Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress />
        </Box>) : hasError ? (<Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Typography color="text.secondary">
            Error in displaying graph data
          </Typography>
        </Box>) : !data || !data.xLabels || data.xLabels.length === 0 ? (
          <Box sx={{ width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Typography color="text.secondary">
              No data available
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, width: "100%", minHeight: 0 }}>
            <BarChart
              series={seriesData}
              xAxis={[{
                data: xLabels,
                scaleType: "band",
                tickLabelStyle: {
                  angle: -45,
                  textAnchor: "end",
                  fontSize: 12,
                  fill: "#333"
                }
              }]}
              height={420}
              margin={{ left: 60, right: 25, top: 35, bottom: 130 }}
              slotProps={{
                legend: {
                  direction: "row",
                  position: { vertical: "top", horizontal: "middle" },
                  padding: 0,
                  itemMarkWidth: 18,
                  itemMarkHeight: 18,
                  markGap: 8,
                  itemGap: 30,
                },
              }}
              yAxis={[{
                valueFormatter: (value: number) => {
                  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
                  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                  return value.toString();
                }
              }]}
              sx={{
                width: "100%",
                "& .MuiChartsAxis-tickLabel": {
                  fontSize: "0.7rem",
                  fill: "#666",
                },
                "& .MuiChartsAxis-line": {
                  stroke: "#e0e0e0",
                },
                "& .MuiChartsGrid-line": {
                  stroke: "#f0f0f0",
                },
                // Basic x-axis label styling
                "& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel": {
                  fontSize: "0.75rem",
                  fill: "#333",
                },
                // Improve legend positioning and spacing
                "& .MuiChartsLegend-root": {
                  transform: "translateY(-10px)",
                  "& .MuiChartsLegend-series": {
                    fontSize: "0.875rem",
                    marginRight: "30px !important",
                    "& text": {
                      fill: "#333 !important",
                      fontWeight: 500,
                    },
                    "& rect": {
                      marginRight: "8px !important",
                    }
                  }
                }
              }}
            />
          </Box>
        )
      }

    </Box>
  )
}